import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { ChartOrderItem } from '../../utils/types/analytics/analytics_types';
import { DashletIconGroup } from './DashletIconGroup';

type AssessmentValueDashletProps = {
  id: string;
  name: string;
  value: string | number;
  dashletItem: ChartOrderItem;
  onDragStart: (item: ChartOrderItem) => void;
  onDragEnter: (item: ChartOrderItem) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  fullScreenDetected: boolean;
  setFullScreenDetected: (fullscreen: boolean) => void;
  handleDashletVisibility: (item: ChartOrderItem) => void;
  editMode: boolean;
};

export const AssessmentValueDashlet = ({
  id,
  name,
  value,
  dashletItem,
  onDragStart,
  onDragEnter,
  onDragOver,
  onDrop,
  fullScreenDetected,
  setFullScreenDetected,
  handleDashletVisibility,
  editMode,
}: AssessmentValueDashletProps) => {
  const FormattedValue = useMemo(() => {
    let formattedValue = '';
    let unit = '';

    if (value !== null && value !== undefined) {
      if (name === 'Quickest Time') {
        formattedValue = String(value);
        unit = 'sec';
      } else if (name === 'Top Score') {
        formattedValue = String(value);
        unit = '%';
      } else {
        formattedValue = String(value);
      }
    } else {
      formattedValue = 'NaN';
    }

    return { formattedValue, unit };
  }, [value, name]);

  const handleDragStart = () => {
    if (onDragStart) {
      onDragStart(dashletItem);
    }
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDragEnter) {
      onDragEnter(dashletItem);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDragOver) {
      onDragOver(event);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDrop) {
      onDrop(event);
    }
  };

  return (
    <Paper
      variant="outlined"
      className="group"
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        minHeight: 180,
        borderRadius: '16px',
        p: 2.5,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',
          borderColor: 'primary.main',
        },
      }}
    >
      <DashletIconGroup
        id={id}
        dashletItem={dashletItem}
        setFullScreenDetected={setFullScreenDetected}
        handleDashletVisibility={handleDashletVisibility}
        editMode={editMode}
      />

      <Box
        id={id}
        draggable
        onDragStart={handleDragStart}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          opacity: dashletItem.hidden ? 0.4 : 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: fullScreenDetected ? '2.25rem' : '0.875rem',
            color: 'text.secondary',
            mb: fullScreenDetected ? 3 : 1.5,
            textAlign: 'center',
          }}
        >
          {name}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'center',
            color: 'primary.main',
            fontWeight: 800,
            lineHeight: 1,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontWeight: 800,
              fontSize: fullScreenDetected
                ? '6rem'
                : typeof value === 'string' || value === undefined || value === null
                  ? '2rem'
                  : '3.5rem',
              color: 'inherit',
              letterSpacing: '-0.03em',
            }}
          >
            {FormattedValue.formattedValue}
          </Typography>
          {FormattedValue.unit && (
            <Typography
              component="span"
              sx={{
                fontWeight: 700,
                fontSize: fullScreenDetected ? '3rem' : '1.75rem',
                color: 'inherit',
                ml: 0.75,
              }}
            >
              {FormattedValue.unit}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};
